import { useState } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    InputAdornment,
    Stack,
} from "@mui/material";

// Custom icons imported from Figma design
import SearchIcon from "@mui/icons-material/Search";
import { FoodsIcon } from "@foodpilot/foods";

import { useTranslation } from "react-i18next";
import { Loading } from "src/components/utils/Loading";
import { useSnackbar } from "src/utils/useSnackbar";
import { useGetAllIngredientLines, IngredientLine } from "src/api/client-api/ingredientLines/useGetAllIngredientLine";
import { useDeleteIngredientLine } from "src/api/client-api/ingredientLines/useDeleteIngredientLine";
import { useCheckIngredientLineIngredients } from "src/api/client-api/ingredientLines/useCheckIngredientLineIngredients";
import { URLHandler } from "src/services/URLHandler";
import { CREATE, INGREDIENT_LINE, useFoodpilotContext } from "src/context/FoodpilotContext.tsx";
import { useNavigate } from "react-router";
import { IngredientLineModal } from "../IngredientLineModal";
import { useQueryClient } from "react-query";
import { AxiosError, isAxiosError } from "axios";
import { SettingsError } from "../SettingsError";

export const IngredientLineList = () => {
    const { canUser, selectedCompany } = useFoodpilotContext();
    const [searchTerm, setSearchTerm] = useState("");

    const { allIngredientLines, isLoading, isError } = useGetAllIngredientLines();
    const deleteIngredientLine = useDeleteIngredientLine();
    const checkIngredientLineIngredients = useCheckIngredientLineIngredients();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const snackbar = useSnackbar();

    const [displayDialog, setDisplayDialog] = useState(false);
    const [idToDelete, setIdToDelete] = useState<number | undefined>(undefined);

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingIngredientLine, setEditingIngredientLine] = useState<IngredientLine | undefined>(undefined);

    // Handle loading state
    if (isLoading) return <Loading />;

    // Handle error state (especially authentication errors)
    if (isError) {
        return (
            <SettingsError
                heading={t("settings.sector", "Sector")}
                message={t(
                    "ingredientLine.error.loadFailed",
                    "Failed to load ingredient lines. Please try refreshing the page or contact support if the problem persists.",
                )}
            />
        );
    }

    const handleDeleteClick = (id: number) => {
        setIdToDelete(id);

        // Check if the ingredient line has associated ingredients
        checkIngredientLineIngredients.mutate(id, {
            onSuccess: (result) => {
                if (result.hasIngredients) {
                    // Show warning that deletion is not possible
                    snackbar.setSnackbarProps({
                        message: t(
                            "ingredientLine.error.cannotDeleteWithIngredients",
                            `Cannot delete this ingredient line because it has ${result.totalIngredients} ingredient(s) associated with it. Please reassign the ingredients to another ingredient line before deleting.`,
                        ),
                        type: "warning",
                    });
                } else {
                    // Safe to delete, show confirmation dialog
                    setDisplayDialog(true);
                }
            },
            onError: () => {
                // If check fails, proceed with deletion attempt (fallback to server-side validation)
                setDisplayDialog(true);
            },
        });
    };

    // Modal handlers
    const handleOpenCreateModal = () => {
        setEditingIngredientLine(undefined);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (ingredientLine: IngredientLine) => {
        setEditingIngredientLine(ingredientLine);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingIngredientLine(undefined);
    };

    const handleModalSuccess = (ingredientLine: IngredientLine) => {
        // Invalidate the ingredient lines cache to refresh the list
        queryClient.invalidateQueries({ queryKey: ["allIngredientLines"] });

        // Show success message
        snackbar.setSnackbarProps({
            message:
                editingIngredientLine ? "Ingredient line updated successfully" : "Ingredient line created successfully",
            type: "success",
        });
    };

    // Filter ingredient lines based on search term
    const filteredIngredientLines =
        allIngredientLines?.filter((ingredientLine) =>
            ingredientLine.name.toLowerCase().includes(searchTerm.toLowerCase()),
        ) || [];

    const renderConfirmDialog = () => {
        return (
            <Dialog maxWidth="xs" open={displayDialog}>
                <DialogTitle>{t("ingredientLine.deleteDialog.title", "Êtes-vous sûr ?")}</DialogTitle>
                <DialogContent dividers>
                    {t(
                        "cliquez_sur_oui_pour_supprimer_definitivement_la_filiere.",
                        "Click yes to permanently delete the ingredient line.",
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDisplayDialog(false)}>{t("non")}</Button>
                    <Button
                        onClick={() => {
                            if (idToDelete) {
                                deleteIngredientLine.mutate(idToDelete, {
                                    onError: (error: AxiosError<object>) => {
                                        if (!isAxiosError(error)) {
                                            return;
                                        }
                                        const errMessage =
                                            (
                                                error?.response?.data &&
                                                "hydra:description" in error.response.data &&
                                                typeof error.response.data["hydra:description"] === "string"
                                            ) ?
                                                error.response.data["hydra:description"]
                                            :   "invalidStringIfNotExist";

                                        if (errMessage.includes("1451")) {
                                            const ingredientScoreUrl =
                                                selectedCompany ? URLHandler.ingredient.list(selectedCompany.id) : "/";
                                            snackbar.setSnackbarProps({
                                                message: (
                                                    <>
                                                        <Typography variant="body2">
                                                            {t(
                                                                "settings.ingredient_line.remove_ingredient_line_error",
                                                                "Cannot delete this ingredient line because it has ingredients associated with it. Please reassign the ingredients to another ingredient line before deleting.",
                                                            )}{" "}
                                                        </Typography>
                                                        <span
                                                            role="button"
                                                            tabIndex={0}
                                                            style={{
                                                                color: "white",
                                                                textDecoration: "underline",
                                                                cursor: "pointer",
                                                            }}
                                                            onClick={() => {
                                                                navigate(
                                                                    `${ingredientScoreUrl}?gamme=${encodeURIComponent(filteredIngredientLines.find((line) => line.id === idToDelete)?.name || "")}`,
                                                                );
                                                                snackbar.forceClose();
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter" || e.key === " ") {
                                                                    navigate(ingredientScoreUrl);
                                                                }
                                                            }}
                                                        >
                                                            <Typography variant="body2">
                                                                {t("menu.products.ingredients", "Ingredients")}
                                                            </Typography>
                                                        </span>
                                                    </>
                                                ),
                                                type: "error",
                                                forceAutoHideInMilisecond: 8000,
                                            });
                                        } else {
                                            snackbar.setSnackbarProps({
                                                message: t(
                                                    "ingredientLine.errorOnDelete",
                                                    "Error deleting ingredient line",
                                                ),
                                                type: "error",
                                            });
                                        }
                                    },
                                    onSuccess: () => {
                                        // Refresh the list after deletion
                                        queryClient.invalidateQueries({ queryKey: ["allIngredientLines"] });
                                        snackbar.setSnackbarProps({
                                            message: t(
                                                "ingredientLine.deleteSuccess",
                                                "Ingredient line deleted successfully",
                                            ),
                                            type: "success",
                                        });
                                    },
                                });
                            }
                            setDisplayDialog(false);
                        }}
                    >
                        {t("oui")}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    return (
        <Box sx={{ padding: "32px" }}>
            <Paper
                elevation={0}
                sx={{
                    border: "1px solid #E7E6E4",
                    borderRadius: "16px",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "32px",
                        borderBottom: "1px solid #E7E6E4",
                        backgroundColor: "white",
                    }}
                >
                    <Box
                        sx={{
                            width: "40px",
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                        }}
                        onClick={() => navigate(-1)}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                "& svg": {
                                    strokeWidth: "2px",
                                    stroke: "currentColor",
                                },
                            }}
                        >
                            <FoodsIcon icon="arrowLeftShort" size={6} />
                        </Box>
                    </Box>
                    <Typography variant="number-inter" sx={{ lineHeight: "1.6em", color: "#393A36" }}>
                        {t("settings.sector", "Sector")}
                    </Typography>
                </Box>

                {/* Search and Add Button Section */}
                <Box
                    sx={{
                        px: 4,
                        py: 3,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    {/* Search Field */}
                    <TextField
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 16, color: "#393A36" }} />
                                </InputAdornment>
                            ),
                            sx: {
                                borderRadius: "24px",
                                backgroundColor: "white",
                                border: "1px solid #E7E6E4",
                                fontSize: "0.875rem",
                                fontWeight: 500,
                                color: "#A3A2A0",
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                },
                                height: "40px",
                                pl: 2.5,
                                pr: 1.5,
                            },
                        }}
                        sx={{ width: 323 }}
                    />

                    {/* Add Ingredient Line Button */}
                    {canUser(CREATE, INGREDIENT_LINE) && (
                        <Button
                            variant="contained"
                            startIcon={<FoodsIcon size={2} icon="plus" />}
                            onClick={handleOpenCreateModal}
                            sx={(theme) => ({
                                borderRadius: "44px",
                                padding: "0px 20px",
                                height: "40px",
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                textTransform: "none",
                                background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                color: "white !important",
                                boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                                "&:hover": {
                                    background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                    boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                                    color: "white !important",
                                },
                                "&:disabled": {
                                    background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                    opacity: 0.36,
                                    color: "white !important",
                                    boxShadow: "0px 6px 12px 0px rgba(42, 43, 39, 0.12)",
                                },
                            })}
                        >
                            {t("ajouter_une_filiere", "Add an ingredient line")}
                        </Button>
                    )}
                </Box>

                {/* Content area */}
                <Box sx={{ padding: "0px 32px 24px" }}>
                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                            border: "1px solid #E7E6E4",
                            borderRadius: "8px",
                            overflow: "hidden",
                        }}
                    >
                        <Table sx={{ "& .MuiTableCell-root": { border: "none" } }}>
                            <TableHead>
                                <TableRow
                                    sx={{
                                        backgroundColor: "#FEFCFB",
                                        "& .MuiTableCell-root": {
                                            borderBottom: "1px solid #E7E6E4",
                                            borderRight: "1px solid #E7E6E4",
                                            padding: "0px 14px 0px 20px",
                                            height: "48px",
                                            "&:last-child": {
                                                borderRight: "none",
                                            },
                                        },
                                    }}
                                >
                                    <TableCell>
                                        <Typography
                                            variant="small-medium"
                                            sx={{ lineHeight: "1.67em", color: "#767673" }}
                                        >
                                            {t("nom_de_la_filiere", "Ingredient Line Name")}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="small-medium"
                                            sx={{ lineHeight: "1.67em", color: "#767673" }}
                                        >
                                            {t("actions", "Actions")}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredIngredientLines.length === 0 ?
                                    <TableRow>
                                        <TableCell colSpan={2} sx={{ textAlign: "center", py: 4 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: "0.875rem",
                                                    color: "#767673",
                                                }}
                                            >
                                                {searchTerm ?
                                                    t(
                                                        "ingredientLine.noSearchResults",
                                                        "No ingredient lines found matching your search.",
                                                    )
                                                :   t(
                                                        "ingredientLine.noIngredientLines",
                                                        "No ingredient lines available. Create your first ingredient line to get started.",
                                                    )
                                                }
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                :   filteredIngredientLines.map((ingredientLine, index) => (
                                        <TableRow
                                            key={ingredientLine.id}
                                            sx={{
                                                borderBottom: "1px solid #E7E6E4",
                                                "&:last-child": {
                                                    borderBottom: "none",
                                                },
                                                "& .MuiTableCell-root": {
                                                    borderRight: "1px solid #E7E6E4",
                                                    padding: "0px 14px 0px 20px",
                                                    height: "58px",
                                                    "&:last-child": {
                                                        borderRight: "none",
                                                        padding: "0px 8px",
                                                    },
                                                },
                                                "&:hover": {
                                                    backgroundColor: "#F9F9F8",
                                                },
                                            }}
                                        >
                                            {/* Ingredient Line Name */}
                                            <TableCell sx={{ borderBottom: "none" }}>
                                                <Typography
                                                    variant="small-medium"
                                                    sx={{
                                                        lineHeight: "22px",
                                                        color: "#1E1E1E",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {ingredientLine.name}
                                                </Typography>
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell sx={{ borderBottom: "none" }}>
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    {ingredientLine.canModify && (
                                                        <Box
                                                            component="button"
                                                            onClick={() => handleOpenEditModal(ingredientLine)}
                                                            sx={{
                                                                width: "32px",
                                                                height: "32px",
                                                                borderRadius: "50%",
                                                                border: "none",
                                                                backgroundColor: "transparent",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                cursor: "pointer",
                                                                transition: "background-color 0.2s",
                                                                "&:hover": {
                                                                    backgroundColor: "#F5F5F5",
                                                                },
                                                            }}
                                                        >
                                                            <FoodsIcon icon="edit" size={1} />
                                                        </Box>
                                                    )}
                                                    {ingredientLine.canDelete && (
                                                        <Box
                                                            component="button"
                                                            onClick={() => handleDeleteClick(ingredientLine.id)}
                                                            sx={{
                                                                width: "32px",
                                                                height: "32px",
                                                                borderRadius: "50%",
                                                                border: "none",
                                                                backgroundColor: "transparent",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                cursor: "pointer",
                                                                transition: "background-color 0.2s",
                                                                "&:hover": {
                                                                    backgroundColor: "#F5F5F5",
                                                                },
                                                            }}
                                                        >
                                                            <FoodsIcon icon="delete" size={1} />
                                                        </Box>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Paper>

            {renderConfirmDialog()}

            {/* Ingredient Line Modal */}
            <IngredientLineModal
                open={isModalOpen}
                onClose={handleCloseModal}
                ingredientLine={editingIngredientLine}
                onSuccess={handleModalSuccess}
            />
        </Box>
    );
};
